import React from 'react';
import { Check } from '../FormControls';
import Field from '../Field';
import {
  SETTINGS_WORDING as W, MAKEUP_CAP_WHEN_TURNED_ON, MAKEUP_EXPIRY_DAYS_WHEN_TURNED_ON,
  makeupCapDraft, makeupExpiryDraft, sendingNotice, sendingNoticeText, sendingNoticeParts, emailSettingsBlurb,
} from '../../domain/studioSettings';
import { BRAND } from '../../domain/brand';
import { useCommittedText, useCommittedValue, blurOnEnter } from './useCommitted';
import CustomEmailTemplates from './CustomEmailTemplates';
import s from './settings.module.css';

// Business Settings → Email & SMS.
//
// ── What MMS has ───────────────────────────────────────────────────────────
//
// The panel opens with "Email Settings" and, verbatim:
//
//   Send emails from My Music Staff using a generic business email address.
//   Your studio name will automatically be used as the sender.
//
//   Studio Email Address (Optional)
//   [ ] Use studio name as default sender
//   [ ] Use studio name & email on reminders
//   [ ] Use studio name & email on lesson notes
//   [ ] Send birthday emails
//
// then a "Public Email Provider" notice with a live From / Reply-To preview,
// then three template libraries: System Email Templates, System SMS Templates
// and Custom Email Templates.
//
// The wording below is MMS's with the product name swapped: it is our screen
// describing our sender.
//
// Not here: Send birthday emails, since nothing scans for a birthday; and the
// System Email and SMS Templates — studio-editable templates with %Placeholder%
// interpolation, Preview and "Restore Default Template" — which are code in
// functions/delivery/templates.js. Custom Email Templates is here (#608,
// CustomEmailTemplates.jsx), after the sending identity, where MMS's comes
// after its two System template lists.
//
// ── What the four sending-identity controls do (#73) ───────────────────────
//
// The split is MMS's own wording — "name" in the first box, "name & email" in
// the other two:
//
//   * Use studio name as default sender puts the studio's name in front of
//     the deployment's generic address, on every message, as the blurb at the
//     top of this panel promises.
//   * Use studio name & email on reminders / on lesson notes put the studio's
//     own address on those kinds as well, which is what produces the Reply-To
//     in the preview below.
//
// The decision is functions/delivery/deliver.js `senderIdentity`, gated: a
// studio whose identity we have no evidence for — an unverified trial — never
// reaches an envelope, because a Reply-To a stranger chooses would make our
// authenticated sending domain a phishing relay (billing/entitled.js
// `senderMayDeliver`).
//
// ── Nothing on this panel configures SMS ───────────────────────────────────
//
// The tab is "Email & SMS" because that is MMS's name for it. Which transport a
// deployment talks to, and its credentials, are deployment facts and not studio
// data (the top of config.js): MESSAGE_SMS_TRANSPORT, TWILIO_ACCOUNT_SID and
// the rest live in functions/.env, where a teacher cannot reach them by writing
// to Firestore.
//
// The sending-identity controls have no SMS half because a text has no From
// header and no Reply-To: it comes from the Twilio number, and every `sms`
// template opens with the studio name (twilio.js ignores the `from` it is
// handed).
//
// MMS's SMS invoice-notification switch is on Accounts & Invoices, and ours is
// too (InvoicingPanel.jsx), although the value lives in the `messaging` group,
// as `sendInvoiceReminders` does. Stored shape and screen placement are
// separate questions.
//
// ── The Make-Up Credit Policy section ──────────────────────────────────────
//
// MMS puts "Make-Up Credit Policy" — "These settings allow you to fine-tune
// your make-up policy", with "Limit number of make-up credits" and "Expire
// unused make-up credits" — on Business Settings → Policies. Here it is a
// section of this panel; both settings do something (src/domain/makeup.js).

// What the toggle starts at when a studio turns a limit on for the first time:
// ours, since MMS's starting values are unrecorded. Non-destructive: a cap of 0
// would mean "this studio issues no credits", the last thing a teacher ticking
// "Limit number of make-up credits" means. `MAKEUP_CAP_WHEN_TURNED_ON` and
// `MAKEUP_EXPIRY_DAYS_WHEN_TURNED_ON` in src/domain/studioSettings.js.

export default function MessagingPanel({
  messaging, makeup, studioName, onChangeMessaging, onChangeMakeup,
}) {
  // Only what changed — the provider merges into the stored group. Passing the
  // whole object would carry this render's stale copy of the other fields.
  const setMessaging = (patch) => onChangeMessaging(patch);
  const setMakeup = (patch) => onChangeMakeup(patch);

  // Text and number fields are committed when they are left, not on every
  // keystroke; the checkboxes commit at once, because a tick has no
  // intermediate state. useCommitted.js holds both rules, including re-seeding
  // from the record when it changes underneath, so a save on a phone lands
  // here rather than being shadowed by what this browser last showed.
  const email = useCommittedText(
    messaging.studioEmail, (studioEmail) => setMessaging({ studioEmail }),
  );

  // Through the domain's own normaliser, so the box shows what was stored: 5000
  // typed into a cap that clamps at 999 comes back 999.
  //
  // Emptying either box commits null — "no cap" / "never expires" — and the
  // checkbox unticks: the same state the checkbox sets.
  const cap = useCommittedValue(
    makeup.maxCredits,
    (draft) => makeupCapDraft(draft, makeup),
    (maxCredits) => setMakeup({ maxCredits }),
  );
  const days = useCommittedValue(
    makeup.expireAfterDays,
    (draft) => makeupExpiryDraft(draft, makeup),
    (expireAfterDays) => setMakeup({ expireAfterDays }),
  );

  // Live, and computed by the function the Cloud Function's transport calls
  // (src/domain/sendingIdentity.js, mirrored into functions/domain/), so this
  // cannot describe an envelope the sender does not build. It follows the field
  // as it is typed rather than the saved value.
  //
  // `sendingNotice` (src/domain/studioSettings.js) is that computation and the
  // notice's choices together: whether it shows, its title, and the envelope.
  const notice = sendingNotice({
    studioName,
    studioEmail: email.field.value,
    brandName: BRAND.name,
    // The deployment's verified address (MESSAGE_FROM_EMAIL) is a Cloud
    // Functions environment variable, not studio data, so a teacher cannot
    // change it by writing to Firestore and the browser cannot read it. So the
    // address is described rather than invented; MMS can print
    // `noreply+GwLJP@mymusicstaff.com` because it mints one per studio.
    // It is set in functions/.env.example.
  });
  const noticeText = sendingNoticeParts(notice.rewritten ? sendingNoticeText({ ...notice, brandName: BRAND.name }) : '');
  // Every studio address is rewritten, so the list decides only how to explain
  // it. A free provider publishes a policy that refuses us outright; any other
  // domain has not been authenticated here. MMS draws the same two cases, under
  // "Public Email Provider" and "SPF Not Configured / DKIM Not Configured" (#73).

  return (
    <div className={s.panel}>
      <div className={s.panelTitle}>{W.emailSettings}</div>
      <p className={s.blurb}>{emailSettingsBlurb(BRAND.name)}</p>

      <div className={s.group}>
        <Field label={W.studioEmail} htmlFor="studio-email" hint={W.optional}>
          <input
            id="studio-email"
            type="email"
            className={s.input}
            placeholder={W.studioEmailPlaceholder}
            {...email.field}
            onKeyDown={blurOnEnter}
          />
        </Field>

        <Check
          checked={messaging.useStudioNameAsSender}
          onChange={(e) => setMessaging({ useStudioNameAsSender: e.target.checked })}
          label={W.useStudioName}
          // The distinction the two labels below draw, said once: this box is
          // the name only. The address stays the generic one the blurb above
          // describes until one of the "name & email" boxes is ticked.
          help={W.useStudioNameHelp}
        />
        <Check
          checked={messaging.useStudioIdentityOnReminders}
          onChange={(e) => setMessaging({ useStudioIdentityOnReminders: e.target.checked })}
          label={W.identityOnReminders}
          help={W.identityOnRemindersHelp}
        />
        <Check
          checked={messaging.useStudioIdentityOnLessonNotes}
          onChange={(e) => setMessaging({ useStudioIdentityOnLessonNotes: e.target.checked })}
          label={W.identityOnLessonNotes}
        />
      </div>

      {notice.rewritten ? (
        <div className={s.notice}>
          <div className={s.noticeTitle}>{notice.title}</div>
          {/* The words are `sendingNoticeText`'s, shared with the phones;
              `sendingNoticeParts` gives "as" back its italics here. */}
          <p className={s.noticeText}>
            {noticeText.before}
            {noticeText.emphasis ? <em>{noticeText.emphasis}</em> : null}
            {noticeText.after}
          </p>
          <dl className={s.envelope}>
            <dt className={s.envelopeLabel}>{W.envelopeFrom}</dt>
            <dd className={s.envelopeValue}>
              {notice.fromName}{' '}
              <span className={s.envelopeUnknown}>{W.envelopeUnknown}</span>
            </dd>
            <dt className={s.envelopeLabel}>{W.envelopeReplyTo}</dt>
            <dd className={s.envelopeValue}>{notice.replyTo}</dd>
          </dl>
        </div>
      ) : null}

      <CustomEmailTemplates />

      {/* MMS's Policies tab section, on this panel. See the header. */}
      <div className={s.section}>
        <div className={s.panelTitle}>{W.makeupPolicy}</div>
        <p className={s.blurb}>
          {W.makeupPolicyBlurb}
        </p>

        <div className={s.group}>
          <Check
            checked={makeup.maxCredits != null}
            onChange={(e) => setMakeup({ maxCredits: e.target.checked ? MAKEUP_CAP_WHEN_TURNED_ON : null })}
            label={W.makeupCapCheck}
            help={W.makeupCapHelp}
          />
          {makeup.maxCredits != null ? (
            <div className={s.policyValue}>
              <input
                type="number"
                min="0"
                max="999"
                aria-label="Maximum make-up credits per student"
                className={s.numberInput}
                {...cap.field}
                onKeyDown={blurOnEnter}
              />
              <span className={s.unit}>{W.makeupCapUnit}</span>
            </div>
          ) : null}

          <Check
            checked={makeup.expireAfterDays != null}
            onChange={(e) => setMakeup({
              expireAfterDays: e.target.checked ? MAKEUP_EXPIRY_DAYS_WHEN_TURNED_ON : null,
            })}
            label={W.makeupExpiryCheck}
            help={W.makeupExpiryHelp}
          />
          {makeup.expireAfterDays != null ? (
            <div className={s.policyValue}>
              <input
                type="number"
                min="1"
                max="3650"
                aria-label="Days before an unused make-up credit expires"
                className={s.numberInput}
                {...days.field}
                onKeyDown={blurOnEnter}
              />
              <span className={s.unit}>{W.makeupExpiryUnit}</span>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
